import { useState, useEffect, useCallback, useRef } from 'react'
import dayjs from 'dayjs'
import { useLocation, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import RefreshIcon from '@mui/icons-material/Refresh'
import { alpha } from '@mui/material/styles'
import api from 'services/api'
import SplitPanes, {
  opensDetail,
  useOpenItem,
} from 'components/core/SplitPanes'
import { describe } from '../Staff/MicRail'
import UserDetail from './UserDetail'
import { MicChip, displayName, fmtStreamed, plural } from './vocabulary'

// The Users page, after reclip-proto-api's /ops#/people: everyone who has
// streamed (every account holding a span row, which is 90 days), freshest
// heartbeat first, beside one user opened in full. A row is the account,
// its microphone as the Staff page's rail reads it (live, stopped and how,
// or never beaten), its last 30 days in two numbers and its friends now (the
// window said once, in the header, not on every row), and under the mic, in
// red, the reports about them or what they said still open, which stack
// inside the row's three lines rather than growing it. On production other
// staff are left off, the Moments page's rule, kept by the server; develop
// and staging list everyone. `?user=` names the user open, so a link
// opens them, whether or not their row is on the loaded page, and on a phone
// opens straight onto them. The header stays pinned over the two panes.
//
// Nothing re-reads on its own: the list is read when the page opens, a page
// more as its end scrolls into view, and the user when they are picked. The refresh button reads both again, the list
// from its first page, and each pane says when it was read.

const PAGE = 50

const UserRow = ({ user, picked, onPick }) => {
  const ref = useRef(null)
  // The arrow keys move the pick past the edge of the view; follow it.
  useEffect(() => {
    if (picked) ref.current?.scrollIntoView({ block: 'nearest' })
  }, [picked])
  return (
    <Paper
      ref={ref}
      variant="outlined"
      onClick={onPick}
      sx={(theme) => ({
        p: 1.25,
        borderRadius: 2,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        ...(picked
          ? {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            }
          : { '&:hover': { backgroundColor: theme.palette.action.hover } }),
      })}
    >
      <Avatar src={user.picture || undefined} sx={{ width: 36, height: 36 }}>
        {(user.full_name || user.username || '?')[0].toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography variant="subtitle2" noWrap>
            {displayName(user)}
          </Typography>
          {user.full_name && user.username && (
            <Typography variant="caption" color="text.secondary" noWrap>
              @{user.username}
            </Typography>
          )}
          {user.is_guest && (
            <Chip
              size="small"
              variant="outlined"
              label="guest"
              sx={{ height: 18, fontSize: 11 }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" component="div">
          {describe(user)}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {fmtStreamed(user.streamed_ms)} streamed ·{' '}
          {plural(user.quotes, 'moment')} ·{' '}
          {plural(user.friends ?? 0, 'friend')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <MicChip presence={user} />
        {user.open_reports > 0 && (
          <Chip
            size="small"
            color="error"
            label={plural(user.open_reports, 'open report')}
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
      </Box>
    </Paper>
  )
}

const Users = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const [pickedId, setPickedId] = useState(() => searchParams.get('user'))
  const [shown, setShown] = useState(() => Boolean(searchParams.get('user')))
  const openItem = useOpenItem()
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState(null)
  const [nextBefore, setNextBefore] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  // Each press of refresh, for the open user's pane to read again too.
  const [refreshes, setRefreshes] = useState(0)
  // As on the Moments page: a first page starts a generation and a read
  // answered after a newer one began is dropped; the cursor asked for keeps
  // the list's end from asking twice.
  const generation = useRef(0)
  const askedBefore = useRef(null)

  const load = useCallback(async (before) => {
    if (!before) generation.current += 1
    const mine = generation.current
    setLoading(true)
    try {
      const data = await api.streamMetrics.getUsers({ limit: PAGE, before })
      if (mine !== generation.current) return
      // Beats move while the list is read, so a user can rise onto a page
      // already shown; they keep the place they were first listed at.
      setRows((current) => {
        if (!before) return data.users
        const seen = new Set(current.map((user) => user.user_id))
        return [
          ...current,
          ...data.users.filter((user) => !seen.has(user.user_id)),
        ]
      })
      setNextBefore(data.next_before)
      setMeta(data)
      setError(null)
    } catch (err) {
      if (mine === generation.current) setError(err.message)
    } finally {
      if (mine === generation.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(null)
  }, [load])

  // The list's end reads the next page; a failed one is offered again at
  // the foot rather than asked for on its own.
  const loadMore = useCallback(() => {
    if (!nextBefore || askedBefore.current === nextBefore) return
    askedBefore.current = nextBefore
    load(nextBefore)
  }, [load, nextBefore])
  const retryMore = () => {
    askedBefore.current = null
    loadMore()
  }

  const picked = pickedId || rows[0]?.user_id || null
  // The state goes along, so a step on a phone keeps the open user's history
  // entry (`SplitPanes`).
  const pick = (userId) => {
    setPickedId(userId)
    setSearchParams({ user: userId }, { replace: true, state: location.state })
  }
  const index = rows.findIndex((user) => user.user_id === picked)
  const move = (delta) => {
    const next = rows[index + delta]
    if (next) pick(next.user_id)
  }
  const step = (event) => {
    const delta = { ArrowDown: 1, ArrowUp: -1 }[event.key]
    if (!delta) return
    event.preventDefault()
    move(delta)
  }

  const header = (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Users
        </Typography>
        <Tooltip title="Read the list and the open user again">
          <span>
            <IconButton
              size="small"
              onClick={() => {
                load(null)
                setRefreshes((count) => count + 1)
              }}
              disabled={loading}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {loading && <CircularProgress size={18} />}
        <Box sx={{ flex: 1 }} />
        {meta && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
            >
              {meta.total} {meta.total === 1 ? 'user' : 'users'} who streamed in
              the last 90 days · freshest heartbeat first
              {meta.staff_excluded
                ? ' · other staff hidden (production)'
                : ' · staff included (develop/staging)'}{' '}
              · as of {dayjs(meta.as_of).format('HH:mm:ss')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
            >
              streamed and moments stats are over the last {meta.history_days}{' '}
              days
            </Typography>
          </Box>
        )}
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {meta?.truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The list stopped at the {plural(meta.total, 'newest account')}: past
          that the heartbeat order needs an index (the server says so in
          Sentry).
        </Alert>
      )}
    </>
  )

  return (
    <SplitPanes
      header={header}
      shown={shown && Boolean(picked)}
      // A phone's only: back to the list leaves no user open, so the URL
      // names none, and a return to the list (back from another page) lands
      // on the list rather than opening the last user again.
      onBack={() => {
        setShown(false)
        setSearchParams({}, { replace: true, state: location.state })
      }}
      onStep={move}
      position={{ index, count: rows.length }}
      backLabel="Users"
      onEnd={nextBefore && !loading ? loadMore : null}
      list={
        <>
          {!loading && !error && rows.length === 0 && !picked && (
            <Typography variant="body2" color="text.secondary" sx={{ my: 3 }}>
              Nobody has streamed in the last 90 days.
            </Typography>
          )}
          <Box
            tabIndex={0}
            onKeyDown={step}
            sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
          >
            {rows.map((user) => (
              <UserRow
                key={user.user_id}
                user={user}
                picked={user.user_id === picked}
                onPick={(event) => {
                  pick(user.user_id)
                  if (opensDetail(event)) {
                    openItem(`?user=${user.user_id}`)
                    setShown(true)
                  }
                }}
              />
            ))}
          </Box>
          {rows.length > 0 && (loading || (error && nextBefore)) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              {loading ? (
                <CircularProgress size={18} />
              ) : (
                <Button onClick={retryMore}>Try again</Button>
              )}
            </Box>
          )}
        </>
      }
      detail={
        picked && (
          <UserDetail key={picked} userId={picked} refreshes={refreshes} />
        )
      }
    />
  )
}

export default Users
